<template>
  <div class="instagram-tool-page">
    <HeadNav selectedLanguage="en" page="tools" />

    <section class="hero" aria-labelledby="tool-hero-title">
      <div class="hero__content">
        <div class="hero__panel">
          <div class="hero__header">
            <h1 id="tool-hero-title" class="hero__title">{{ h1Title }}</h1>
            <p class="hero__subtitle">{{ subtitle }}</p>
            <p class="hero__note">{{ limitationNote }}</p>
          </div>
          <form class="hero__form" @submit.prevent="handleSubmit">
            <input
              v-model="pasteUrl"
              class="hero__input"
              type="text"
              :placeholder="inputPlaceholder"
              :aria-label="inputPlaceholder"
            >
            <button class="hero__button" type="submit" :disabled="isParsing">
              {{ isParsing ? loadingLabel : actionLabel }}
            </button>
          </form>
          <p v-if="parseError" class="hero__error" role="alert">{{ parseError }}</p>
          <div
            v-if="parseResult && parseResult.media && parseResult.media.length"
            class="ig-result"
          >
            <div class="ig-result__header">
              <strong>
                {{ viewMode ? 'Preview' : 'Download ready' }} —
                {{ parseResult.mediaCount }} item{{ parseResult.mediaCount > 1 ? 's' : '' }}
              </strong>
              <span v-if="parseResult.username">@{{ parseResult.username }}</span>
            </div>
            <div class="ig-result__list">
              <div
                v-for="(item, index) in parseResult.media"
                :key="item.url + index"
                class="ig-result__item"
              >
                <video
                  v-if="item.type === 'video'"
                  class="ig-result__media"
                  :src="item.url"
                  :poster="item.thumbnail"
                  controls
                  playsinline
                  preload="metadata"
                />
                <img
                  v-else
                  class="ig-result__media"
                  :src="item.url"
                  :alt="'Instagram media ' + (index + 1)"
                >
                <div class="ig-result__body">
                  <span>{{ getMediaTypeLabel(item.type) }} {{ index + 1 }}</span>
                  <a
                    v-if="showDownloadActions && !viewMode"
                    class="ig-result__download"
                    :href="getDownloadUrl(item.url, item.type)"
                  >Download</a>
                  <a
                    v-else-if="viewMode"
                    class="ig-result__download"
                    :href="item.url"
                    target="_blank"
                    rel="noopener noreferrer"
                  >Open media</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <div class="tool-page-wrap">
      <div class="tool-page-stack">
        <slot />
        <section v-if="relatedTools.length" class="tool-section">
          <h2>Related Instagram tools</h2>
          <p>Working with a different type of Instagram content? Try another SnapInsta tool:</p>
          <ul>
            <li v-for="tool in relatedTools" :key="tool.path">
              <router-link :to="tool.path">{{ tool.label }}</router-link>
            </li>
          </ul>
        </section>
        <section v-if="faqs && faqs.length" class="tool-section tool-section--faq">
          <h2>FAQ</h2>
          <FAQ :faqs="faqs" />
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import '@/css/base.scss';
import '@/css/index.scss';
import '@/css/instagram-tool.scss';
import HeadNav from '@/components/HeadNav.vue';
import FAQ from '@/components/FAQ.vue';
import axios from 'axios';
import { getRelatedTools } from '@/data/instagram-related-tools';

export default {
  name: 'InstagramToolPage',
  components: {
    HeadNav,
    FAQ,
  },
  props: {
    h1Title: {
      type: String,
      required: true,
    },
    subtitle: {
      type: String,
      required: true,
    },
    inputPlaceholder: {
      type: String,
      default: 'Paste Instagram URL here',
    },
    actionLabel: {
      type: String,
      default: 'Download',
    },
    loadingLabel: {
      type: String,
      default: 'Working...',
    },
    limitationNote: {
      type: String,
      default: 'Public Instagram content only.',
    },
    viewMode: {
      type: Boolean,
      default: false,
    },
    showDownloadActions: {
      type: Boolean,
      default: true,
    },
    currentToolPath: {
      type: String,
      required: true,
    },
    faqs: {
      type: Array,
      default: () => [],
    },
  },
  data () {
    return {
      pasteUrl: '',
      isParsing: false,
      parseResult: null,
      parseError: '',
    };
  },
  computed: {
    relatedTools () {
      return getRelatedTools(this.currentToolPath);
    },
  },
  methods: {
    normalizeInput (raw) {
      const value = (raw || '').trim();
      if (!value) {
        return '';
      }
      if (value.includes('instagram.com')) {
        return value;
      }
      const username = value.replace(/^@/, '').replace(/\s+/g, '');
      if (username && /^[a-zA-Z0-9._]+$/.test(username)) {
        return `https://www.instagram.com/${username}/`;
      }
      return value;
    },
    getMediaTypeLabel (type) {
      return type === 'video' ? 'Video' : 'Image';
    },
    getDownloadUrl (url, type) {
      return `/api/instagram/download?url=${encodeURIComponent(url)}&type=${encodeURIComponent(type)}`;
    },
    async handleSubmit () {
      const raw = (this.pasteUrl || '').trim();
      if (!raw) {
        this.parseError = this.viewMode
          ? 'Enter a username or paste a public Instagram link first.'
          : 'Please paste an Instagram link first.';
        return;
      }

      const url = this.normalizeInput(raw);

      if (this.viewMode && !raw.includes('instagram.com') && !raw.includes('/p/') && !raw.includes('/reel')) {
        this.parseError =
          'Profile-only viewing is limited here. Paste a link to a specific public post or Reel, or open the profile on instagram.com in your browser.';
        return;
      }

      if (!url.includes('instagram.com')) {
        this.parseError = 'Please enter a valid Instagram username or URL.';
        return;
      }

      const shortcodePattern = /instagram\.com\/(?:.*\/)?(?:p|reels?|tv)\/[a-zA-Z0-9_-]+/;
      if (!shortcodePattern.test(url)) {
        this.parseError =
          'This tool needs a direct link to a public post, Reel, Story highlight, or legacy IGTV URL—not a profile page alone.';
        return;
      }

      this.isParsing = true;
      this.parseError = '';
      this.parseResult = null;

      try {
        const res = await axios.post('/api/instagram/parse', { url }, {
          headers: { 'Content-Type': 'application/json' },
        });
        this.parseResult = res.data;
      } catch (error) {
        this.parseError =
          (error.response && error.response.data && error.response.data.message) ||
          error.message ||
          'Could not load this content. It may be private, expired, or unavailable.';
      } finally {
        this.isParsing = false;
      }
    },
  },
};
</script>

<style lang="scss">
.instagram-tool-page .tool-section h2,
.instagram-tool-page .tool-section h3 {
	color: #186dff !important;
}
</style>
